/**
 * CategoryRulesModal
 *
 * Lets an admin view, add, and delete the exact-match auto-categorization
 * rules (bank_category_rules) for a single accounting category.
 *
 * Save flow (handleAdd)
 * ─────────────────────
 * 1. Persist the new rule via `bankCategoryRulesService.createRule`.
 * 2. Show a simple success toast — NO automatic dialog.
 *
 * Back-fill flow (handleChipClick)
 * ─────────────────────────────────
 * Clicking the text area of any existing rule chip:
 * 1. Sets `scanningId` → shows a spinner on that chip.
 * 2. Calls `findUnclassifiedMatching` for the chip's description.
 * 3. Opens <ApplyRuleToHistoryDialog> if matches are found;
 *    shows an info toast if none are found.
 *
 * Unique-constraint violations (same description already mapped to another
 * category in this company) are caught and surfaced as friendly toasts.
 */
import { useState, useEffect, useRef, KeyboardEvent, useCallback } from 'react';
import { X, Plus, Trash2, Loader2, Tag, AlertCircle, History } from 'lucide-react';
import {
  bankCategoryRulesService,
  BankCategoryRule,
} from '../../lib/bankCategoryRulesService';
import { BankTransaction } from '../../types/database';
import { emitToast } from '../ui/Toast';
import { ApplyRuleToHistoryDialog } from './ApplyRuleToHistoryDialog';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CategoryRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  categoryId: string;
  categoryName: string;
  /**
   * Optional: called after a successful back-fill so the parent can
   * refresh any tables that display bank transactions.
   */
  onTransactionsUpdated?: (updatedCount: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CategoryRulesModal({
  isOpen,
  onClose,
  companyId,
  categoryId,
  categoryName,
  onTransactionsUpdated,
}: CategoryRulesModalProps) {
  // ── Rules list ────────────────────────────────────────────────────────────
  const [rules, setRules]           = useState<BankCategoryRule[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);

  // ── Add-rule input ────────────────────────────────────────────────────────
  const [inputValue, setInputValue] = useState('');
  const [adding, setAdding]         = useState(false);

  // ── Delete ────────────────────────────────────────────────────────────────
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ── Manual back-fill scan (triggered by clicking a chip) ─────────────────
  // scanningId: the rule.id currently being scanned (shows spinner on chip).
  const [scanningId, setScanningId] = useState<string | null>(null);

  // ── Back-fill dialog state ────────────────────────────────────────────────
  const [historyDialogOpen, setHistoryDialogOpen]       = useState(false);
  const [historyTransactions, setHistoryTransactions]   = useState<BankTransaction[]>([]);
  const [historyDescription, setHistoryDescription]     = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  // ── Load rules whenever modal opens ───────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    void loadRules();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [isOpen, companyId, categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await bankCategoryRulesService.getRulesForCategory(
      companyId,
      categoryId,
    );
    if (fetchError) {
      setError(fetchError.message);
    } else {
      setRules(data ?? []);
    }
    setLoading(false);
  };

  // ── Add rule — saves only, no automatic dialog ──────────────────────────
  const handleAdd = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    // Optimistic duplicate-check for this category
    if (rules.some((r) => r.exact_description === trimmed)) {
      emitToast('ניסוח זה כבר מוגדר עבור סיווג זה', 'error');
      return;
    }

    setAdding(true);
    setError(null);

    const { data: newRule, error: createError } = await bankCategoryRulesService.createRule({
      company_id: companyId,
      category_id: categoryId,
      exact_description: trimmed,
    });

    setAdding(false);

    if (createError) {
      // 23505 = unique constraint: description already assigned to another category
      if (
        createError.code === '23505' ||
        (createError.details as { code?: string } | undefined)?.code === '23505'
      ) {
        emitToast('ניסוח זה כבר מוגדר במערכת לסעיף אחר', 'error');
      } else {
        emitToast(createError.message ?? 'שגיאה בשמירת הכלל', 'error');
      }
      return;
    }

    if (newRule) {
      setRules((prev) =>
        [...prev, newRule].sort((a, b) =>
          a.exact_description.localeCompare(b.exact_description, 'he'),
        ),
      );
      setInputValue('');
    }

    emitToast('הכלל נוסף בהצלחה — לחץ על הכלל כדי לסווג עסקאות קיימות', 'success');
  };

  // ── Chip click — manual back-fill trigger ─────────────────────────────────
  // Clicking the text portion of a rule chip scans historical transactions
  // and opens the ApplyRuleToHistoryDialog if any unclassified matches exist.
  const handleChipClick = useCallback(async (rule: BankCategoryRule) => {
    // Prevent double-click while a scan is already running
    if (scanningId) return;

    setScanningId(rule.id);

    const { data: matchingTxs, error: scanError } =
      await bankCategoryRulesService.findUnclassifiedMatching(
        companyId,
        rule.exact_description,
        categoryId,           // exclude txs already under THIS category
      );

    setScanningId(null);

    if (scanError) {
      emitToast(scanError.message ?? 'שגיאה בחיפוש עסקאות', 'error');
      return;
    }

    if (!matchingTxs || matchingTxs.length === 0) {
      emitToast('לא נמצאו עסקאות לא מסווגות התואמות לכלל זה', 'info');
      return;
    }

    // Found matches — open the back-fill dialog
    setHistoryDescription(rule.exact_description);
    setHistoryTransactions(matchingTxs);
    setHistoryDialogOpen(true);
  }, [scanningId, companyId]);

  // ── Back-fill confirmed ───────────────────────────────────────────────────
  const handleHistoryApplied = (updatedCount: number) => {
    emitToast(
      `${updatedCount} עסקאות סווגו בהצלחה`,
      'success',
    );
    onTransactionsUpdated?.(updatedCount);
  };

  // ── Delete rule ───────────────────────────────────────────────────────────
  const handleDelete = async (ruleId: string) => {
    setDeletingId(ruleId);
    const { error: deleteError } = await bankCategoryRulesService.deleteRule(ruleId);
    setDeletingId(null);

    if (deleteError) {
      emitToast(deleteError.message ?? 'שגיאה במחיקת הכלל', 'error');
      return;
    }

    setRules((prev) => prev.filter((r) => r.id !== ruleId));
    emitToast('הכלל נמחק', 'success');
  };

  // Enter key submits the input
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleAdd();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* ── Main rules modal ──────────────────────────────────────────────── */}
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crm-title"
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">

          {/* ── Header ───────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-primary-600" />
              <h2 id="crm-title" className="text-base font-semibold text-gray-900">
                כללי זיהוי אוטומטי
              </h2>
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
                {categoryName}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="סגור"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* ── Description ──────────────────────────────────────────────── */}
          <p className="px-5 pt-3 pb-2 text-xs text-gray-500 flex-shrink-0">
            כאשר תיאור עסקת בנק מיובאת תואם <strong>בדיוק</strong> אחד מהניסוחים הבאים,
            המערכת תשייך אותה אוטומטית לסיווג זה.
            {' '}<span className="text-indigo-600 font-medium">לחץ על כלל קיים</span> כדי
            לסווג עסקאות היסטוריות שטרם סווגו.
          </p>

          {/* ── Error Banner ─────────────────────────────────────────────── */}
          {error && (
            <div className="mx-5 mb-2 flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 flex-shrink-0">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── Add-rule input ────────────────────────────────────────────── */}
          <div className="px-5 pb-3 flex-shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='הכנס ניסוח מדויק, לדוגמה: "שכר דירה ינואר 2025"'
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md
                  focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  placeholder:text-gray-400 text-right"
                disabled={adding}
              />
              <button
                onClick={() => void handleAdd()}
                disabled={adding || !inputValue.trim()}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white
                  bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50
                  disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                {adding
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Plus className="h-3.5 w-3.5" />}
                {adding ? 'שומר…' : 'הוסף'}
              </button>
            </div>
            {adding && (
              <p className="text-xs text-gray-400 mt-1.5 text-right">
                שומר כלל…
              </p>
            )}
          </div>

          {/* ── Rules list (chips) ────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
              </div>
            ) : rules.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                <Tag className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p>אין כללים עדיין</p>
                <p className="text-xs mt-1">הוסף ניסוח ראשון למעלה</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 pt-1">
                {rules.map((rule) => {
                  const isScanning = scanningId === rule.id;
                  const isDeleting = deletingId === rule.id;
                  // Disable chip click while any scan or delete is in flight
                  const chipDisabled = isScanning || !!scanningId || isDeleting;

                  return (
                    <span
                      key={rule.id}
                      className="inline-flex items-center gap-0 bg-blue-50 border border-blue-200
                        text-blue-800 rounded-full text-xs font-medium max-w-full
                        overflow-hidden"
                    >
                      {/* ── Clickable text area ─────────────────────────── */}
                      <button
                        type="button"
                        onClick={() => void handleChipClick(rule)}
                        disabled={chipDisabled}
                        title="לחץ לסיווג עסקאות קיימות"
                        className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1
                          rounded-r-full hover:bg-blue-100 active:bg-blue-200
                          transition-colors disabled:cursor-not-allowed
                          disabled:opacity-60 focus:outline-none
                          focus-visible:ring-2 focus-visible:ring-indigo-400"
                        aria-label={`סווג עסקאות לפי "${rule.exact_description}"`}
                      >
                        {isScanning
                          ? <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                          : <History className="h-3 w-3 flex-shrink-0 opacity-50" />}
                        <span className="truncate max-w-[230px]" title={rule.exact_description}>
                          {rule.exact_description}
                        </span>
                      </button>

                      {/* ── Divider ─────────────────────────────────────── */}
                      <span className="w-px self-stretch bg-blue-200 flex-shrink-0" />

                      {/* ── Delete button ───────────────────────────────── */}
                      <button
                        type="button"
                        onClick={() => void handleDelete(rule.id)}
                        disabled={isDeleting || isScanning}
                        className="flex-shrink-0 px-1.5 py-1 rounded-l-full
                          hover:bg-red-100 hover:text-red-600 transition-colors
                          disabled:opacity-50 disabled:cursor-not-allowed
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                        aria-label={`מחק "${rule.exact_description}"`}
                      >
                        {isDeleting
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Trash2 className="h-3 w-3" />}
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Footer ───────────────────────────────────────────────────── */}
          <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
            <span className="text-xs text-gray-400">
              {rules.length} {rules.length === 1 ? 'כלל' : 'כללים'} מוגדרים
            </span>
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300
                rounded-md hover:bg-gray-50 transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      </div>

      {/* ── Back-fill dialog (z-60, rendered on top of this modal) ────────── */}
      <ApplyRuleToHistoryDialog
        isOpen={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        onApplied={handleHistoryApplied}
        categoryId={categoryId}
        categoryName={categoryName}
        exactDescription={historyDescription}
        transactions={historyTransactions}
      />
    </>
  );
}
